/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


/*******************************************************************************
 ** Pure model operations for the GridBuilder widget.
 **
 ** Structure - which grids, columns, rows and sub-rows exist, and their stable
 ** ids - lives here.  Values do NOT: they live in the parent Formik context,
 ** keyed by the namespaced names this module generates.  Functions that need
 ** values take them as an argument, which keeps one source of truth for values
 ** and lets the whole module be tested without React.
 *******************************************************************************/

export type GridBuilderAxis = "column" | "row" | "cell";


/*******************************************************************************
 ** the one grid a widget that predates the grids dimension ever has.
 **
 ** ONLY such a widget holds a grid here.  A widget using the grids dimension
 ** gives every one of its grids an allocated id - it may hold no grids at all -
 ** so gridIds, not this, is what says which grids exist.  Keeping the two kinds
 ** of widget in disjoint id spaces is what tells the pre-grids payload shape
 ** apart from the grids one: see usesGridsDimension.  Two widgets sharing one
 ** form are kept apart by their widget names - see widgetSegment.
 **
 ** Zero is never handed out as an id (ids start at 1), and it is the one grid id
 ** that contributes nothing to a field name - which is what keeps a
 ** single-grid widget's field names identical to what they were before grids
 ** existed.
 *******************************************************************************/
export const DEFAULT_GRID_ID = 0;


/*******************************************************************************
 ** the columns, rows and cells of ONE grid
 *******************************************************************************/
export interface GridBuilderGridAxes
{
   columnIds: number[];
   rowIds: number[];

   //////////////////////////////////////////////////////////////////////
   // keyed by cellKey(rowId, columnId) - the sub-row ids in that cell //
   //////////////////////////////////////////////////////////////////////
   subRowIds: Record<string, number[]>;
}


/*******************************************************************************
 ** a whole widget's worth of structure.
 **
 ** The structure IS the default grid's axes - columnIds, rowIds and subRowIds
 ** mean exactly what they meant before grids existed - and additionalGridAxes
 ** carries one entry per grid beyond the default.  Read either through
 ** getGridAxes rather than reaching into additionalGridAxes, so a caller does
 ** not have to know which of the two a grid id lands in.
 *******************************************************************************/
export interface GridBuilderStructure extends GridBuilderGridAxes
{
   nextId: number;

   /////////////////////////////////////////////////////////////////////////////////////
   // every grid, in display order.  buildOutputJson emits one entry per id here, so  //
   // an empty list is a widget with no grids at all - which a widget using the grids //
   // dimension may legitimately be.                                                  //
   /////////////////////////////////////////////////////////////////////////////////////
   gridIds: number[];

   //////////////////////////////////////////////////////////////////////////////
   // keyed by gridId - holds NO entry for DEFAULT_GRID_ID, whose axes are the //
   // structure's own                                                          //
   //////////////////////////////////////////////////////////////////////////////
   additionalGridAxes: Record<number, GridBuilderGridAxes>;

   ///////////////////////////////////////////////////////////////////////////////////
   // which widget these names belong to - see widgetSegment.  Absent for a         //
   // structure built without one, which then generates exactly the names it always //
   // did.                                                                          //
   ///////////////////////////////////////////////////////////////////////////////////
   widgetName?: string;
}


export interface GridBuilderOrderBy
{
   fieldName: string;
   isAscending: boolean;
}


export interface GridBuilderUniqueKey
{
   fieldNames: string[];
   label?: string;
}


export interface UniqueKeyViolation
{
   axis: GridBuilderAxis;
   ids: number[];
   fieldNames: string[];
   label?: string;
   gridId?: number;
}


/*******************************************************************************
 ** one value that appears in the grid headers of several grids, and the grids
 ** that claim it.  The value is carried so a message can name it, which is the
 ** only part of this a user can act on.
 *******************************************************************************/
export interface GridHeaderOverlap
{
   fieldName: string;
   value: any;
   gridIds: number[];
}


export interface GridBuilderFieldNames
{
   ////////////////////////////////////////////////////////////////////////////////////
   // absent or empty for a widget with one grid and nothing to say about it - which //
   // is also what makes buildOutputJson emit the pre-grids payload shape.           //
   ////////////////////////////////////////////////////////////////////////////////////
   gridHeader?: string[];

   columnHeader: string[];
   rowHeader: string[];
   cell: string[];
}


/*******************************************************************************
 ** a field, reduced to the parts of it a message about it needs.  QFieldMetaData
 ** satisfies this, so callers pass their field lists straight through.
 *******************************************************************************/
export interface GridBuilderMessageField
{
   name: string;
   label?: string;
   isRequired?: boolean;
}


export interface GridBuilderMessageFields
{
   gridHeader?: GridBuilderMessageField[];
   columnHeader: GridBuilderMessageField[];
   rowHeader: GridBuilderMessageField[];
   cell: GridBuilderMessageField[];
}


/*******************************************************************************
 ** the configured nouns for the axes - "Carton Type", "Temp. Band", "TNT Rule",
 ** "Item".  grid is optional because a widget with one grid never names it.
 *******************************************************************************/
export interface GridBuilderAxisLabels
{
   grid?: string;
   column: string;
   row: string;
   subRow: string;
}


/*******************************************************************************
 ** a required field left blank, and enough about where in the grid it is for the
 ** user to go find it
 *******************************************************************************/
export interface MissingRequiredValue
{
   location: string;
   fieldLabel: string;
}


/*******************************************************************************
 ** how to name one value in a message.
 **
 ** A header value is routinely a possible-value id, and "Carton Type 163" is
 ** neither what the user picked nor what the input in front of them shows.  This
 ** module cannot resolve one: it takes values and never labels, and knows
 ** nothing of possible-value sources - so a caller that CAN resolve one hands
 ** this in, and this module only ever calls it.
 **
 ** Both names are passed because a caller's labels may be keyed by either: by
 ** the FIELD, for labels resolved once for every input that field has, and by
 ** the INPUT's namespaced name, for a label that arrived with one particular
 ** input's value.  A multi-value input holds several ids in its one value, and
 ** naming that value - splitting it and joining the labels - is the resolver's
 ** to do, since only the caller knows which of its fields are multi-value.
 *******************************************************************************/
export type GridBuilderValueLabelResolver = (fieldName: string, inputName: string, value: any) => string;


/*******************************************************************************
 ** the resolver of a caller with no labels to offer: every value names itself.
 **
 ** This is what findMissingRequiredValues defaults to, so a caller that passes
 ** no resolver gets exactly the messages this module has always produced.
 *******************************************************************************/
export function resolveValueAsItself(fieldName: string, inputName: string, value: any): string
{
   return (String(value));
}


/*******************************************************************************
 ** key identifying one cell, by its row and column
 *******************************************************************************/
export function cellKey(rowId: number, columnId: number): string
{
   return (`${rowId}:${columnId}`);
}


/*******************************************************************************
 ** the part of a field name that says which grid it belongs to.
 **
 ** Empty for the default grid, so a widget with one grid produces exactly the
 ** names it produced before grids existed - the saved payloads, the customizers
 ** that parse them, and the tests that assert them all depend on those names.
 *******************************************************************************/
function gridSegment(gridId: number): string
{
   return (gridId === DEFAULT_GRID_ID ? "" : `_g${gridId}`);
}


/*******************************************************************************
 ** the part of a field name that says which WIDGET it belongs to.
 **
 ** Two grid widgets on one screen write into ONE Formik form, and every id in a
 ** name comes from a counter its own widget starts at 1 - so two widgets
 ** configured alike name one Formik key for two different inputs, each reading
 ** back the other's values, and each re-seed nulling out every cumulative name it
 ** does not find in its own payload.  Nothing about a grid, a row or a column can
 ** tell the two apart; only the widget can.
 **
 ** Trails the rest of the name rather than leading it, so a name still BEGINS
 ** with the field it belongs to - which is how these inputs are found by
 ** everything that looks for one, from the record-screen tests to a developer
 ** reading the elements panel.
 **
 ** Empty for a structure built without a widget name, which then generates
 ** exactly the names this module has always generated.
 **
 ** Only the three characters Formik cannot carry are escaped - it reads "." and
 ** "[" / "]" in a field name as a path into a nested object, so a widget named
 ** with one would have its values written somewhere other than the key they were
 ** named for.  Each maps to its own token rather than all of them collapsing to
 ** an underscore, because collapsing would make two DIFFERENTLY named widgets
 ** ("a.b" and "a-b") generate one set of names - the collision this segment
 ** exists to prevent.  Every other character is left alone, so names stay
 ** readable and two distinct widget names stay distinct.
 *******************************************************************************/
function widgetSegment(widgetName: string): string
{
   if (isBlank(widgetName))
   {
      return ("");
   }

   ///////////////////////////////////////////////////////////////////////////////////
   // "_" is escaped FIRST, and that ordering is the whole point: escaping only the //
   // three path characters would map both "a.b" and "a_dot_b" onto one segment, so //
   // two differently named widgets would share a namespace - the collision this    //
   // segment exists to prevent.  Escaping the escape character first makes the     //
   // mapping injective, so distinct names stay distinct.                           //
   ///////////////////////////////////////////////////////////////////////////////////
   const escaped = String(widgetName)
      .replace(/_/g, "_us_")
      .replace(/\./g, "_dot_")
      .replace(/\[/g, "_lb_")
      .replace(/]/g, "_rb_");

   return (`_w${escaped}`);
}


/*******************************************************************************
 ** the widget a structure's names belong to, reading a structure built without
 ** one as no widget segment at all
 *******************************************************************************/
function widgetNameOf(structure: GridBuilderStructure): string
{
   return (structure.widgetName ?? "");
}


/*******************************************************************************
 ** Formik field name for a grid header input.
 **
 ** Unlike the other three, this one always carries its grid segment: grid
 ** headers are new, so there is no earlier name to stay compatible with, and the
 ** bare field name could collide with a field of the same name on the record the
 ** widget is embedded in.
 *******************************************************************************/
export function gridFieldName(fieldName: string, gridId: number, widgetName: string = ""): string
{
   return (`${fieldName}_g${gridId}${widgetSegment(widgetName)}`);
}


/*******************************************************************************
 ** Formik field name for a column header input
 *******************************************************************************/
export function columnFieldName(fieldName: string, columnId: number, gridId: number = DEFAULT_GRID_ID, widgetName: string = ""): string
{
   return (`${fieldName}${gridSegment(gridId)}_c${columnId}${widgetSegment(widgetName)}`);
}


/*******************************************************************************
 ** Formik field name for a row header input
 *******************************************************************************/
export function rowFieldName(fieldName: string, rowId: number, gridId: number = DEFAULT_GRID_ID, widgetName: string = ""): string
{
   return (`${fieldName}${gridSegment(gridId)}_r${rowId}${widgetSegment(widgetName)}`);
}


/*******************************************************************************
 ** Formik field name for an input within a cell's sub-row
 *******************************************************************************/
export function cellFieldName(fieldName: string, rowId: number, columnId: number, subRowId: number, gridId: number = DEFAULT_GRID_ID, widgetName: string = ""): string
{
   return (`${fieldName}${gridSegment(gridId)}_r${rowId}_c${columnId}_s${subRowId}${widgetSegment(widgetName)}`);
}


/*******************************************************************************
 ** whether a widget's grids all carry allocated ids, rather than the one grid a
 ** widget that predates the dimension holds at DEFAULT_GRID_ID.
 **
 ** DEFAULT_GRID_ID is reserved for a widget with exactly ONE grid and no grid
 ** header fields.  It is the one id contributing nothing to a field name, so
 ** anything that can hold more than one grid has to stay off it: one widget
 ** holding grid 0 alongside allocated grids 1..n reads as the pre-grids shape by
 ** its first grid and as the grids shape by the rest, so there is no one payload
 ** for it to publish.  (Two SEPARATE widgets are kept apart by their widget
 ** names instead - see widgetSegment.)
 **
 ** Either signal is enough on its own.  Grid header fields mean the user has been
 ** given something to say about a grid, which is a widget presenting grids as a
 ** dimension whatever its counts are.  expectedGridCount is how many grids the
 ** widget may end up holding - the largest of its initialGrids, its minGrids and
 ** however many its data describes - and more than one of those cannot fit in the
 ** single-grid shape.
 *******************************************************************************/
export function usesAllocatedGridIds(gridHeaderFieldCount: number, expectedGridCount: number): boolean
{
   return (gridHeaderFieldCount > 0 || expectedGridCount > 1);
}


/*******************************************************************************
 ** a widget with nothing in it.
 **
 ** gridHeaderFieldCount and expectedGridCount say which kind of widget this is,
 ** taken here as parameters because at this point there is no structure yet to
 ** read it from - see usesAllocatedGridIds, which is the same decision every
 ** other caller makes.  A widget whose grids carry allocated ids starts with NO
 ** grids: the grids it ends up with are the ones its data described or the user
 ** added.  Every other widget has exactly one grid, at DEFAULT_GRID_ID, whose
 ** field names carry no grid segment.
 **
 ** Two grid widgets share one form because every name carries the widget it
 ** belongs to - see widgetSegment.  That the two kinds hold grids at disjoint ids
 ** is a separate rule about ONE widget: DEFAULT_GRID_ID is the one id
 ** contributing nothing to a field name, and it is reserved for the widget whose
 ** single grid publishes the pre-grids payload.
 **
 ** expectedGridCount defaults to one - the count at which the decision rests
 ** entirely on the grid header fields, which is what every caller that predates
 ** this parameter meant.  widgetName defaults to none, which generates the names
 ** this module has always generated.
 *******************************************************************************/
export function emptyStructure(gridHeaderFieldCount: number = 0, expectedGridCount: number = 1, widgetName: string = ""): GridBuilderStructure
{
   const gridIds = usesAllocatedGridIds(gridHeaderFieldCount, expectedGridCount) ? [] : [DEFAULT_GRID_ID];
   return ({columnIds: [], rowIds: [], subRowIds: {}, nextId: 1, gridIds: gridIds, additionalGridAxes: {}, widgetName: widgetName});
}


/*******************************************************************************
 ** shallow copy, so callers can treat structures as immutable.
 **
 ** Every grid's axis arrays are copied, not just the default grid's, so a
 ** function that edits one grid in place cannot reach back into the structure it
 ** was given.
 *******************************************************************************/
function copy(structure: GridBuilderStructure): GridBuilderStructure
{
   const additionalGridAxes: Record<number, GridBuilderGridAxes> = {};
   for (const gridId of Object.keys(structure.additionalGridAxes ?? {}))
   {
      additionalGridAxes[Number(gridId)] = copyAxes(structure.additionalGridAxes[Number(gridId)]);
   }

   return ({
      ...copyAxes(structure),
      nextId: structure.nextId,
      gridIds: [...gridIdsOf(structure)],
      additionalGridAxes: additionalGridAxes,
      widgetName: structure.widgetName,
   });
}


/*******************************************************************************
 ** shallow copy of one grid's axes
 *******************************************************************************/
function copyAxes(axes: GridBuilderGridAxes): GridBuilderGridAxes
{
   return ({
      columnIds: [...axes.columnIds],
      rowIds: [...axes.rowIds],
      subRowIds: {...axes.subRowIds},
   });
}


/*******************************************************************************
 ** the grid ids of a structure, defaulting a structure that predates grids to
 ** the one grid it describes
 *******************************************************************************/
function gridIdsOf(structure: GridBuilderStructure): number[]
{
   return (structure.gridIds ?? [DEFAULT_GRID_ID]);
}


/*******************************************************************************
 ** the columns, rows and cells of one grid.
 **
 ** The returned object is the live one, so a caller holding a copy()'d structure
 ** may edit it in place.  A grid id that is not in the structure reads as an
 ** empty grid rather than throwing - a removed grid's ids may still be held by a
 ** render in flight.
 *******************************************************************************/
export function getGridAxes(structure: GridBuilderStructure, gridId: number = DEFAULT_GRID_ID): GridBuilderGridAxes
{
   if (gridId === DEFAULT_GRID_ID)
   {
      return (structure);
   }

   return (structure.additionalGridAxes?.[gridId] ?? {columnIds: [], rowIds: [], subRowIds: {}});
}


/*******************************************************************************
 ** one grid's axes, ready to be edited in place.  Only ever called on a copy.
 *******************************************************************************/
function getGridAxesForWrite(structure: GridBuilderStructure, gridId: number): GridBuilderGridAxes
{
   if (gridId === DEFAULT_GRID_ID)
   {
      return (structure);
   }

   if (!structure.additionalGridAxes[gridId])
   {
      structure.additionalGridAxes[gridId] = {columnIds: [], rowIds: [], subRowIds: {}};
   }

   return (structure.additionalGridAxes[gridId]);
}


/*******************************************************************************
 ** add a grid, with the columns and rows it should start with
 *******************************************************************************/
export function addGrid(structure: GridBuilderStructure, initialColumns: number = 0, initialRows: number = 0, minSubRowsPerCell: number = 0): GridBuilderStructure
{
   let result = copy(structure);

   const gridId = result.nextId++;
   result.gridIds.push(gridId);
   result.additionalGridAxes[gridId] = {columnIds: [], rowIds: [], subRowIds: {}};

   //////////////////////////////////////////////////////////////////////////////
   // columns first, then rows - a row is given a cell per column that already //
   // exists, so building it the other way around leaves the cells behind      //
   //////////////////////////////////////////////////////////////////////////////
   for (let i = 0; i < initialColumns; i++)
   {
      result = addColumn(result, 0, gridId);
   }
   for (let i = 0; i < initialRows; i++)
   {
      result = addRow(result, minSubRowsPerCell, gridId);
   }

   return (result);
}


/*******************************************************************************
 ** remove a grid and everything in it.
 **
 ** Values left behind in Formik are simply not read again, the same way a removed
 ** column's are - buildOutputJson only reads names belonging to the current
 ** structure.
 *******************************************************************************/
export function removeGrid(structure: GridBuilderStructure, gridId: number): GridBuilderStructure
{
   const result = copy(structure);
   result.gridIds = result.gridIds.filter(id => id !== gridId);
   delete result.additionalGridAxes[gridId];

   if (gridId === DEFAULT_GRID_ID)
   {
      result.columnIds = [];
      result.rowIds    = [];
      result.subRowIds = {};
   }

   return (result);
}


/*******************************************************************************
 ** is this value blank for sorting and uniqueness purposes.
 **
 ** exported because GridBuilderWidget uses the same definition when testing
 ** whether a prerequisite field has been filled in.
 *******************************************************************************/
export function isBlank(value: any): boolean
{
   return (value === null || value === undefined || value === "");
}


/*******************************************************************************
 ** how a multi-value input holds its several ids inside the ONE value the rest
 ** of the grid is built around: joined with a comma, so buildOutputJson emits a
 ** plain string and the backend's recordFromJson parse needs no notion of a
 ** multi-valued field.
 **
 ** An id containing a comma would not survive the round trip.  These are
 ** possible-value ids, in practice integer primary keys, so that is a stated
 ** limit rather than a case to encode around.
 *******************************************************************************/
export const MULTIPLE_VALUE_DELIMITER = ",";


/*******************************************************************************
 ** the ids held in one multi-value input's single value.
 **
 ** A numeric id is restored as a number, and only when the text round-trips
 ** exactly: an option's id arrives from JSON as a number, and DynamicSelect
 ** matches a seeded value to an option with ===, so a string "1" would leave
 ** option 1 looking unselected and let the user pick it a second time.  A source
 ** whose ids are genuinely strings ("007", "abc") fails that check and is left
 ** as it is.
 *******************************************************************************/
export function splitMultipleValue(value: any): any[]
{
   if (isBlank(value))
   {
      return ([]);
   }

   return (String(value)
      .split(MULTIPLE_VALUE_DELIMITER)
      .map(id => id.trim())
      .filter(id => id !== "")
      .map(id => (String(Number(id)) === id ? Number(id) : id)));
}


/*******************************************************************************
 ** add a column, giving every existing row a cell for it
 *******************************************************************************/
export function addColumn(structure: GridBuilderStructure, minSubRowsPerCell: number, gridId: number = DEFAULT_GRID_ID): GridBuilderStructure
{
   const result   = copy(structure);
   const axes     = getGridAxesForWrite(result, gridId);
   const columnId = result.nextId++;
   axes.columnIds.push(columnId);

   for (const rowId of axes.rowIds)
   {
      axes.subRowIds[cellKey(rowId, columnId)] = allocateIds(result, minSubRowsPerCell);
   }

   return (result);
}


/*******************************************************************************
 ** remove a column, dropping only that column's cells.  values left behind in
 ** Formik are simply not read again - buildOutputJson only reads names that are
 ** part of the current structure.
 *******************************************************************************/
export function removeColumn(structure: GridBuilderStructure, columnId: number, gridId: number = DEFAULT_GRID_ID): GridBuilderStructure
{
   const result = copy(structure);
   const axes   = getGridAxesForWrite(result, gridId);

   axes.columnIds = axes.columnIds.filter(id => id !== columnId);

   for (const rowId of axes.rowIds)
   {
      delete axes.subRowIds[cellKey(rowId, columnId)];
   }

   return (result);
}


/*******************************************************************************
 ** add a row, with one cell per existing column
 *******************************************************************************/
export function addRow(structure: GridBuilderStructure, minSubRowsPerCell: number, gridId: number = DEFAULT_GRID_ID): GridBuilderStructure
{
   const result = copy(structure);
   const axes   = getGridAxesForWrite(result, gridId);
   const rowId  = result.nextId++;
   axes.rowIds.push(rowId);

   for (const columnId of axes.columnIds)
   {
      axes.subRowIds[cellKey(rowId, columnId)] = allocateIds(result, minSubRowsPerCell);
   }

   return (result);
}


/*******************************************************************************
 ** remove a row and all of its cells
 *******************************************************************************/
export function removeRow(structure: GridBuilderStructure, rowId: number, gridId: number = DEFAULT_GRID_ID): GridBuilderStructure
{
   const result = copy(structure);
   const axes   = getGridAxesForWrite(result, gridId);

   axes.rowIds = axes.rowIds.filter(id => id !== rowId);

   for (const columnId of axes.columnIds)
   {
      delete axes.subRowIds[cellKey(rowId, columnId)];
   }

   return (result);
}


/*******************************************************************************
 ** add a sub-row to one cell
 *******************************************************************************/
export function addSubRow(structure: GridBuilderStructure, rowId: number, columnId: number, gridId: number = DEFAULT_GRID_ID): GridBuilderStructure
{
   const result = copy(structure);
   const axes   = getGridAxesForWrite(result, gridId);
   const key    = cellKey(rowId, columnId);

   axes.subRowIds[key] = [...(axes.subRowIds[key] ?? []), result.nextId++];

   return (result);
}


/*******************************************************************************
 ** remove one sub-row from one cell
 *******************************************************************************/
export function removeSubRow(structure: GridBuilderStructure, rowId: number, columnId: number, subRowId: number, gridId: number = DEFAULT_GRID_ID): GridBuilderStructure
{
   const result = copy(structure);
   const axes   = getGridAxesForWrite(result, gridId);
   const key    = cellKey(rowId, columnId);

   axes.subRowIds[key] = (axes.subRowIds[key] ?? []).filter(id => id !== subRowId);

   return (result);
}


/*******************************************************************************
 ** allocate a run of fresh ids, advancing the structure's counter
 *******************************************************************************/
function allocateIds(structure: GridBuilderStructure, count: number): number[]
{
   const ids: number[] = [];
   for (let i = 0; i < count; i++)
   {
      ids.push(structure.nextId++);
   }
   return (ids);
}


/*******************************************************************************
 ** compare two header values - numbers numerically, anything else as strings,
 ** numbers before non-numbers, blanks last.
 **
 ** Deciding numeric-vs-lexical per PAIR alone is not a total order once a column
 ** mixes the two: "9" < "10" numerically, "10" < "1a" lexically, "1a" < "9"
 ** lexically - a cycle, whose resulting order is then whatever the sort algorithm
 ** happens to land on.  isAxisSorted re-sorts and compares, so it could disagree
 ** with what the user is looking at, leaving the "Sort now" alert up after they
 ** had already clicked it and clicking again doing nothing visible.  Ranking all
 ** numbers ahead of all non-numbers removes the cycle.
 *******************************************************************************/
function compareValues(a: any, b: any): number
{
   if (isBlank(a) && isBlank(b))
   {
      return (0);
   }
   if (isBlank(a))
   {
      return (1);
   }
   if (isBlank(b))
   {
      return (-1);
   }

   const numberA  = Number(a);
   const numberB  = Number(b);
   const aIsANumber = !isNaN(numberA);
   const bIsANumber = !isNaN(numberB);

   if (aIsANumber && bIsANumber)
   {
      return (numberA - numberB);
   }
   if (aIsANumber)
   {
      return (-1);
   }
   if (bIsANumber)
   {
      return (1);
   }

   return (String(a).localeCompare(String(b)));
}


/*******************************************************************************
 ** the ids on an axis of one grid
 *******************************************************************************/
function axisIds(structure: GridBuilderStructure, axis: GridBuilderAxis, gridId: number = DEFAULT_GRID_ID): number[]
{
   const axes = getGridAxes(structure, gridId);
   return (axis === "column" ? axes.columnIds : axes.rowIds);
}


/*******************************************************************************
 ** name of a header field for one id on an axis
 *******************************************************************************/
function axisFieldName(axis: GridBuilderAxis, fieldName: string, id: number, gridId: number = DEFAULT_GRID_ID, widgetName: string = ""): string
{
   return (axis === "column" ? columnFieldName(fieldName, id, gridId, widgetName) : rowFieldName(fieldName, id, gridId, widgetName));
}


/*******************************************************************************
 ** order an axis by the given orderBys.  cells are keyed by (rowId, columnId), so
 ** they follow their row and column without being moved.
 *******************************************************************************/
export function sortAxis(structure: GridBuilderStructure, values: Record<string, any>, orderBys: GridBuilderOrderBy[], axis: GridBuilderAxis, gridId: number = DEFAULT_GRID_ID): GridBuilderStructure
{
   const widgetName = widgetNameOf(structure);
   const result     = copy(structure);
   const axes       = getGridAxesForWrite(result, gridId);
   const sorted     = [...axisIds(structure, axis, gridId)].sort((idA, idB) =>
   {
      for (const orderBy of orderBys ?? [])
      {
         const comparison = compareValues(
            values[axisFieldName(axis, orderBy.fieldName, idA, gridId, widgetName)],
            values[axisFieldName(axis, orderBy.fieldName, idB, gridId, widgetName)]);

         if (comparison !== 0)
         {
            return (orderBy.isAscending ? comparison : -comparison);
         }
      }
      return (0);
   });

   if (axis === "column")
   {
      axes.columnIds = sorted;
   }
   else
   {
      axes.rowIds = sorted;
   }

   return (result);
}


/*******************************************************************************
 ** is this axis already in the order its rule asks for
 *******************************************************************************/
export function isAxisSorted(structure: GridBuilderStructure, values: Record<string, any>, orderBys: GridBuilderOrderBy[], axis: GridBuilderAxis, gridId: number = DEFAULT_GRID_ID): boolean
{
   const current = axisIds(structure, axis, gridId);
   const sorted  = axisIds(sortAxis(structure, values, orderBys, axis, gridId), axis, gridId);
   return (JSON.stringify(current) === JSON.stringify(sorted));
}


/*******************************************************************************
 ** find unique-key violations on an axis.
 **
 ** Every scope is the narrowest one the user would recognize as a duplicate.
 ** Grids are scoped separately - two override grids legitimately hold the same
 ** max temp, and only a repeat WITHIN one grid is a collision.  Within a grid,
 ** "cell" narrows further still: each cell is evaluated on its own, so the same
 ** values in two different cells are not a violation.  Tuples containing any
 ** blank value are skipped, since a half-entered row is a required-field problem
 ** rather than a duplicate.
 *******************************************************************************/
export function findUniqueKeyViolations(structure: GridBuilderStructure, values: Record<string, any>, uniqueKeys: GridBuilderUniqueKey[], axis: GridBuilderAxis): UniqueKeyViolation[]
{
   const violations: UniqueKeyViolation[] = [];
   const widgetName                       = widgetNameOf(structure);

   for (const uniqueKey of uniqueKeys ?? [])
   {
      for (const gridId of gridIdsOf(structure))
      {
         const axes = getGridAxes(structure, gridId);

         if (axis === "cell")
         {
            for (const rowId of axes.rowIds)
            {
               for (const columnId of axes.columnIds)
               {
                  collectDuplicates(
                     axes.subRowIds[cellKey(rowId, columnId)] ?? [],
                     (subRowId, fieldName) => values[cellFieldName(fieldName, rowId, columnId, subRowId, gridId, widgetName)],
                     uniqueKey, axis, gridId, violations);
               }
            }
         }
         else
         {
            collectDuplicates(
               axisIds(structure, axis, gridId),
               (id, fieldName) => values[axisFieldName(axis, fieldName, id, gridId, widgetName)],
               uniqueKey, axis, gridId, violations);
         }
      }
   }

   return (violations);
}


/*******************************************************************************
 ** add one violation per group of ids sharing the same non-blank tuple
 *******************************************************************************/
function collectDuplicates(ids: number[], readValue: (id: number, fieldName: string) => any, uniqueKey: GridBuilderUniqueKey, axis: GridBuilderAxis, gridId: number, violations: UniqueKeyViolation[]): void
{
   const idsByTuple: Record<string, number[]> = {};

   for (const id of ids)
   {
      const tuple = uniqueKey.fieldNames.map(fieldName => readValue(id, fieldName));
      if (tuple.some(isBlank))
      {
         continue;
      }

      const tupleKey = JSON.stringify(tuple.map(value => String(value)));
      idsByTuple[tupleKey] = [...(idsByTuple[tupleKey] ?? []), id];
   }

   for (const tupleKey of Object.keys(idsByTuple))
   {
      if (idsByTuple[tupleKey].length > 1)
      {
         violations.push({axis, ids: idsByTuple[tupleKey], fieldNames: uniqueKey.fieldNames, label: uniqueKey.label, gridId: gridId});
      }
   }
}


/*******************************************************************************
 ** find the values that more than one grid's header claims.
 **
 ** A grid header says what a whole grid applies to, so a value in it is one of
 ** those things - and two grids holding the same value both claim it, with
 ** nothing to say which of them wins.  Every message about a grid also names it
 ** by its header values, so two grids claiming the same value are two grids no
 ** message can tell apart.
 **
 ** Deliberately NOT a unique key.  A GridBuilderUniqueKey compares whole values,
 ** so "1,2" and "2,3" do not collide even though the id 2 is in both - which is
 ** precisely the mistake to catch.  A value is read as the delimited SET it is,
 ** and each id in it is compared on its own.
 **
 ** A blank header claims nothing, so two blanks are not a collision.
 *******************************************************************************/
export function findGridHeaderOverlaps(structure: GridBuilderStructure, values: Record<string, any>, fieldNames: string[]): GridHeaderOverlap[]
{
   const overlaps: GridHeaderOverlap[] = [];
   const widgetName                    = widgetNameOf(structure);

   for (const fieldName of fieldNames ?? [])
   {
      //////////////////////////////////////////////////////////////////////////////////
      // keyed by the id as a string, so 2 and "2" are read as the same claim - which //
      // they are, one having arrived from the backend and the other from a pick.     //
      //////////////////////////////////////////////////////////////////////////////////
      const claimsByValue = new Map<string, GridHeaderOverlap>();

      for (const gridId of gridIdsOf(structure))
      {
         for (const id of splitMultipleValue(values[gridFieldName(fieldName, gridId, widgetName)]))
         {
            const valueKey = String(id);
            if (!claimsByValue.has(valueKey))
            {
               claimsByValue.set(valueKey, {fieldName: fieldName, value: id, gridIds: []});
            }

            ///////////////////////////////////////////////////////////////////////
            // one grid naming the same id twice is not an overlap between grids //
            ///////////////////////////////////////////////////////////////////////
            const claim = claimsByValue.get(valueKey);
            if (!claim.gridIds.includes(gridId))
            {
               claim.gridIds.push(gridId);
            }
         }
      }

      claimsByValue.forEach(claim =>
      {
         if (claim.gridIds.length > 1)
         {
            overlaps.push(claim);
         }
      });
   }

   return (overlaps);
}


/*******************************************************************************
 ** the name to call a field by in a message.
 **
 ** A grid may blank a field's label to keep its column tight - a quantity under
 ** an already-labeled header needs no second label - but a message that names the
 ** field still needs something to call it, so fall back to its name, spaced and
 ** capitalized ("quantity" -> "Quantity", "itemId" -> "Item Id").
 *******************************************************************************/
export function fieldDisplayLabel(field: GridBuilderMessageField): string
{
   if (!isBlank(field.label))
   {
      return (field.label);
   }

   const spaced = field.name.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
   return (spaced.charAt(0).toUpperCase() + spaced.slice(1));
}


/*******************************************************************************
 ** how to refer to one column or row in a message.
 **
 ** Prefer the values the user typed in its header - "Temp. Band 40" is how they
 ** think of that column - and fall back to its position when the header is blank,
 ** which is often the case precisely because the missing value IS the header.
 **
 ** A header value the user PICKED rather than typed is an id, so it is named
 ** through the resolver - see GridBuilderValueLabelResolver.  Every axis, not
 ** only the grid one: any of them may hold a possible-value field, and a value
 ** that resolves to nothing is unaffected either way.
 *******************************************************************************/
function describeAxisEntry(fields: GridBuilderMessageField[], values: Record<string, any>, nameOfField: (fieldName: string) => string, label: string, index: number, resolveValueLabel: GridBuilderValueLabelResolver): string
{
   const headerValues = (fields ?? [])
      .map(field => ({fieldName: field.name, inputName: nameOfField(field.name)}))
      .filter(entry => !isBlank(values[entry.inputName]))
      .map(entry =>
      {
         const value         = values[entry.inputName];
         const resolvedLabel = resolveValueLabel(entry.fieldName, entry.inputName, value);

         ///////////////////////////////////////////////////////////////////////////
         // a resolver with no label for a value must not erase it - a value with //
         // no label names itself, which is also what the default resolver does   //
         ///////////////////////////////////////////////////////////////////////////
         return (isBlank(resolvedLabel) ? String(value) : resolvedLabel);
      });

   return (headerValues.length > 0 ? `${label} ${headerValues.join(" / ")}` : `${label} #${index + 1}`);
}


/*******************************************************************************
 ** every required field in the grid that is currently blank, in reading order.
 **
 ** The same rule Yup is enforcing, restated so it can be reported as prose: the
 ** per-input red underline says a value is wrong but not which grid position it
 ** is in, and in a grid of any size that is not enough to act on.
 **
 ** minGrids is the widget's configured floor on how many grids it may have, and
 ** decides only whether a blank default grid is skipped here - see
 ** isGridPublishable.  It defaults to 1, the value at which that rule is inert,
 ** so a caller with nothing to say about grid counts reports what this module
 ** has always reported.
 **
 ** resolveValueLabel names the header values a location is built from, and
 ** defaults to naming each one as itself - which is what a caller that has no
 ** labels, and every caller that predates this parameter, gets.
 *******************************************************************************/
export function findMissingRequiredValues(structure: GridBuilderStructure, values: Record<string, any>, fields: GridBuilderMessageFields, labels: GridBuilderAxisLabels, minGrids: number = 1, resolveValueLabel: GridBuilderValueLabelResolver = resolveValueAsItself): MissingRequiredValue[]
{
   const missingValues: MissingRequiredValue[] = [];
   const namesTheGrid                          = usesGridsDimension(structure, (fields.gridHeader ?? []).length);
   const fieldNames                            = collectFieldNames(fields);
   const widgetName                            = widgetNameOf(structure);

   gridIdsOf(structure).forEach((gridId, gridIndex) =>
   {
      //////////////////////////////////////////////////////////////////////////////////////
      // an added grid with nothing in it anywhere is dropped at publish time rather      //
      // than reported - see isGridPublishable.  Listing a screen full of required        //
      // values for a grid the user has already walked away from is exactly what that     //
      // dropping is for.                                                                 //
      //                                                                                  //
      // The default grid is normally judged as it always has been.  It is not droppable, //
      // so its blank required values are still the user's to fill in - which is what a   //
      // widget with one grid and nothing in it reports.  The exception is a widget whose //
      // grids are all optional, where it is dropped too and there is then nothing left   //
      // to report.                                                                       //
      //////////////////////////////////////////////////////////////////////////////////////
      if (!isGridPublishable(structure, values, gridId, fieldNames, minGrids))
      {
         return;
      }

      const axes = getGridAxes(structure, gridId);

      ///////////////////////////////////////////////////////////////////////////////////
      // a widget with one unnamed grid says nothing about it - "TNT Rule 24 / Temp.   //
      // Band 40" is already unambiguous, and naming a grid the user never chose would //
      // be noise                                                                      //
      ///////////////////////////////////////////////////////////////////////////////////
      const gridDescriptor = describeAxisEntry(fields.gridHeader, values, fieldName => gridFieldName(fieldName, gridId, widgetName), labels.grid ?? "Grid", gridIndex, resolveValueLabel);
      const gridPrefix     = namesTheGrid ? `${gridDescriptor} / ` : "";

      for (const field of fields.gridHeader ?? [])
      {
         if (field.isRequired && isBlank(values[gridFieldName(field.name, gridId, widgetName)]))
         {
            missingValues.push({location: gridDescriptor, fieldLabel: fieldDisplayLabel(field)});
         }
      }

      /////////////////////////////////////////////////////////////////////////////////////
      // described once up front, because every cell in a column refers back to the same //
      // column descriptor.  These are the un-prefixed descriptors: a cell gets its grid //
      // through its row's descriptor, so prefixing here too would name it twice.        //
      /////////////////////////////////////////////////////////////////////////////////////
      const columnDescriptors = new Map<number, string>();
      axes.columnIds.forEach((columnId, index) =>
         columnDescriptors.set(columnId, describeAxisEntry(fields.columnHeader, values, fieldName => columnFieldName(fieldName, columnId, gridId, widgetName), labels.column, index, resolveValueLabel)));

      for (const columnId of axes.columnIds)
      {
         for (const field of fields.columnHeader ?? [])
         {
            if (field.isRequired && isBlank(values[columnFieldName(field.name, columnId, gridId, widgetName)]))
            {
               missingValues.push({location: gridPrefix + columnDescriptors.get(columnId), fieldLabel: fieldDisplayLabel(field)});
            }
         }
      }

      axes.rowIds.forEach((rowId, rowIndex) =>
      {
         const rowDescriptor = gridPrefix + describeAxisEntry(fields.rowHeader, values, fieldName => rowFieldName(fieldName, rowId, gridId, widgetName), labels.row, rowIndex, resolveValueLabel);

         for (const field of fields.rowHeader ?? [])
         {
            if (field.isRequired && isBlank(values[rowFieldName(field.name, rowId, gridId, widgetName)]))
            {
               missingValues.push({location: rowDescriptor, fieldLabel: fieldDisplayLabel(field)});
            }
         }

         for (const columnId of axes.columnIds)
         {
            const subRowIds      = axes.subRowIds[cellKey(rowId, columnId)] ?? [];
            const cellFieldNames = (fields.cell ?? []).map(field => field.name);

            subRowIds.forEach((subRowId, subRowIndex) =>
            {
               ////////////////////////////////////////////////////////////////////////////////////
               // an entirely-blank sub-row is not missing anything - it is a cell the user has  //
               // not put anything in, which is a legitimate state.  Only a sub-row with some of //
               // its fields filled in is incomplete.                                            //
               ////////////////////////////////////////////////////////////////////////////////////
               if (isSubRowBlank(values, cellFieldNames, rowId, columnId, subRowId, gridId, widgetName))
               {
                  return;
               }

               ////////////////////////////////////////////////////////////////////////
               // only number the sub-rows when there is more than one to tell apart //
               ////////////////////////////////////////////////////////////////////////
               const cellDescriptor = `${rowDescriptor} / ${columnDescriptors.get(columnId)}`
                  + (subRowIds.length > 1 ? `, ${labels.subRow} #${subRowIndex + 1}` : "");

               for (const field of fields.cell ?? [])
               {
                  if (field.isRequired && isBlank(values[cellFieldName(field.name, rowId, columnId, subRowId, gridId, widgetName)]))
                  {
                     missingValues.push({location: cellDescriptor, fieldLabel: fieldDisplayLabel(field)});
                  }
               }
            });
         }
      });
   });

   return (missingValues);
}


/*******************************************************************************
 ** whether this widget is using the grids dimension at all.
 **
 ** Two grids obviously are.  So is one grid with grid header fields configured -
 ** the user has been given something to say about the grid, so a message that
 ** omitted it would be inconsistent with the one they see after adding a second.
 ** And so is one grid at an allocated id, which only a widget that may hold more
 ** than one ever has: see usesAllocatedGridIds.
 **
 ** What is left is the single anonymous grid at DEFAULT_GRID_ID, which is the
 ** shape every consumer that predates grids sends.
 *******************************************************************************/
export function usesGridsDimension(structure: GridBuilderStructure, gridHeaderFieldCount: number): boolean
{
   const gridIds = gridIdsOf(structure);
   if (gridHeaderFieldCount > 0)
   {
      return (true);
   }

   //////////////////////////////////////////////////////////////////////////////////////
   // stated as what the pre-grids shape IS - exactly one grid, at DEFAULT_GRID_ID -   //
   // rather than as a list of the grids-dimension cases, so that NO grids counts too. //
   // A widget configured with initialGrids: 2 and no grid header fields allocates ids //
   // and lets the user remove grids down to minGrids, which defaults to 0; reading    //
   // that empty state as the pre-grids shape published {"columns":[],"rows":[]} - the //
   // backend's reading of "one grid with nothing in it", not "no grids" - and hid the //
   // "+ Add Grid" control, so the user could not put one back.                        //
   //                                                                                  //
   // One grid at an ALLOCATED id is likewise a grids-dimension widget, holding the    //
   // single grid it has been left with: publishing the flat payload there would read  //
   // DEFAULT_GRID_ID's names, which such a widget never wrote a value to.             //
   //////////////////////////////////////////////////////////////////////////////////////
   return (gridIds.length !== 1 || gridIds[0] !== DEFAULT_GRID_ID);
}


/*******************************************************************************
 ** the field names behind a set of message fields, for the functions that work
 ** in names rather than in labels
 *******************************************************************************/
function collectFieldNames(fields: GridBuilderMessageFields): GridBuilderFieldNames
{
   return ({
      gridHeader: (fields.gridHeader ?? []).map(field => field.name),
      columnHeader: (fields.columnHeader ?? []).map(field => field.name),
      rowHeader: (fields.rowHeader ?? []).map(field => field.name),
      cell: (fields.cell ?? []).map(field => field.name),
   });
}


/*******************************************************************************
 ** whether one grid belongs in the published payload.
 **
 ** An added grid with nothing in it anywhere is dropped: the user clicked add
 ** and changed their mind, which is not a problem to report and not a grid the
 ** application should have to decide anything about.  A grid with SOMETHING in
 ** it is published as it stands - one whose cells are filled in but whose header
 ** is blank is a real mistake, named by findMissingRequiredValues, rather than
 ** work the user can see on screen being silently discarded.
 **
 ** The grid a widget starts with is normally published even when blank.  It
 ** exists whether or not the user asked for it, so it can never be the grid they
 ** added and abandoned, and dropping it would leave a widget whose one grid is
 ** empty publishing no grids at all - with nothing said about why, since a
 ** minimum of one grid is satisfied by the one that is there.
 *******************************************************************************/
function isGridPublishable(structure: GridBuilderStructure, values: Record<string, any>, gridId: number, fieldNames: GridBuilderFieldNames, minGrids: number): boolean
{
   if (!isGridPrunable(structure, gridId, fieldNames, minGrids))
   {
      return (true);
   }

   return (!isGridBlank(structure, values, gridId, fieldNames));
}


/*******************************************************************************
 ** whether this grid is one that MAY be dropped, if it turns out to be blank.
 **
 ** The structural half of isGridPublishable, split out and exported because
 ** GridBuilderWidget needs the same answer: a grid that will be dropped must not
 ** have its blank required header fields block the save, and a grid that will be
 ** kept must.  Asking this rather than restating it is what keeps the payload and
 ** the validation from ever disagreeing about whether a grid exists.
 **
 ** Says nothing about whether the grid IS blank - that is isGridBlank, and it is
 ** the half that depends on values.  Keeping the two apart lets a caller that
 ** must decide before it has values (registering a Yup schema) still decide
 ** correctly: a grid this returns false for needs no blank-tolerance at all.
 *******************************************************************************/
export function isGridPrunable(structure: GridBuilderStructure, gridId: number, fieldNames: GridBuilderFieldNames, minGrids: number): boolean
{
   /////////////////////////////////////////////////////////////////////////////////////
   // by position rather than by DEFAULT_GRID_ID: only a widget that does not use the //
   // grids dimension holds a grid at that id, so a grids-dimension widget's first    //
   // grid is whichever allocated id it holds first.                                  //
   /////////////////////////////////////////////////////////////////////////////////////
   const isFirstGrid = gridIdsOf(structure)[0] === gridId;

   return (!isFirstGrid || isFirstGridPrunable(structure, fieldNames, minGrids));
}


/*******************************************************************************
 ** whether a widget's blank FIRST grid may be dropped, the way a blank added
 ** grid is.
 **
 ** Only when the grids dimension is in use AND the widget's minGrids is 0 - the
 ** one configuration in which the reasoning above inverts.  There, no grids is a
 ** state the user is entitled to be in ("this matrix overrides nothing"), so an
 ** empty grids array is the correct payload rather than an omission needing
 ** explanation - and keeping the grid would instead make its blank required
 ** values block a save the user never asked to make.
 **
 ** Both halves matter.  minGrids alone would reach a widget with one grid and no
 ** grid header fields, whose minGrids is also 0 by default: that widget has
 ** nothing to say about grids, publishes the flat pre-grids payload, and must
 ** keep reporting its blank required values as it always has.
 *******************************************************************************/
function isFirstGridPrunable(structure: GridBuilderStructure, fieldNames: GridBuilderFieldNames, minGrids: number): boolean
{
   return (minGrids === 0 && usesGridsDimension(structure, (fieldNames.gridHeader ?? []).length));
}


/*******************************************************************************
 ** read a set of field values into a plain object, omitting names with no value
 *******************************************************************************/
function readValues(fieldNames: string[], values: Record<string, any>, nameFor: (fieldName: string) => string): Record<string, any>
{
   const result: Record<string, any> = {};
   for (const fieldName of fieldNames ?? [])
   {
      const value = values[nameFor(fieldName)];
      if (value !== undefined)
      {
         result[fieldName] = value;
      }
   }
   return (result);
}


/*******************************************************************************
 ** build the JSON blob published under the widget's outputFieldName.
 **
 ** A widget using the grids dimension publishes a grids array; anything else
 ** publishes the columns-and-rows object it has always published, which the
 ** backend reads as one grid.  That is not a nicety - every consumer that
 ** predates grids sends and re-parses the flat shape, so emitting grids
 ** unconditionally would change payloads nobody asked to change.
 **
 ** Within a grid, cells is always parallel in length to columns, so an empty cell
 ** is an empty subRows list rather than a hole.  Only names belonging to the
 ** current structure are read, so values left behind by a removed grid, row or
 ** column are ignored - and an added grid with nothing in it is left out
 ** entirely, per isGridPublishable.
 **
 ** minGrids is the widget's configured floor on how many grids it may have, and
 ** decides only whether a blank default grid is left out too - see
 ** isGridPublishable.  It defaults to 1, the value at which that rule is inert,
 ** so a caller with nothing to say about grid counts publishes what this module
 ** has always published.
 *******************************************************************************/
export function buildOutputJson(structure: GridBuilderStructure, values: Record<string, any>, fieldNames: GridBuilderFieldNames, minGrids: number = 1): string
{
   if (!usesGridsDimension(structure, (fieldNames.gridHeader ?? []).length))
   {
      return (JSON.stringify(buildGridJson(structure, values, fieldNames, DEFAULT_GRID_ID)));
   }

   const widgetName = widgetNameOf(structure);
   const grids      = gridIdsOf(structure)
      .filter(gridId => isGridPublishable(structure, values, gridId, fieldNames, minGrids))
      .map(gridId => ({
         header: readValues(fieldNames.gridHeader, values, fieldName => gridFieldName(fieldName, gridId, widgetName)),
         ...buildGridJson(structure, values, fieldNames, gridId),
      }));

   return (JSON.stringify({grids}));
}


/*******************************************************************************
 ** the columns and rows of one grid, as the backend reads them
 *******************************************************************************/
function buildGridJson(structure: GridBuilderStructure, values: Record<string, any>, fieldNames: GridBuilderFieldNames, gridId: number): { columns: any[], rows: any[] }
{
   const axes       = getGridAxes(structure, gridId);
   const widgetName = widgetNameOf(structure);

   const columns = axes.columnIds.map(columnId => ({
      header: readValues(fieldNames.columnHeader, values, fieldName => columnFieldName(fieldName, columnId, gridId, widgetName)),
   }));

   const rows = axes.rowIds.map(rowId => ({
      header: readValues(fieldNames.rowHeader, values, fieldName => rowFieldName(fieldName, rowId, gridId, widgetName)),
      cells: axes.columnIds.map(columnId => ({
         /////////////////////////////////////////////////////////////////////////////////////
         // entirely-blank sub-rows are dropped rather than published as objects full of    //
         // nulls.  A grid starts every new cell with one empty sub-row so it can be filled //
         // in without clicking first, which means most cells in a grid the user only       //
         // partly fills are empty - and the application would otherwise be handed a        //
         // sub-row per untouched cell to tell apart from real ones.                        //
         /////////////////////////////////////////////////////////////////////////////////////
         subRows: (axes.subRowIds[cellKey(rowId, columnId)] ?? [])
            .filter(subRowId => !isSubRowBlank(values, fieldNames.cell, rowId, columnId, subRowId, gridId, widgetName))
            .map(subRowId => readValues(fieldNames.cell, values, fieldName => cellFieldName(fieldName, rowId, columnId, subRowId, gridId, widgetName))),
      })),
   }));

   return ({columns, rows});
}


/*******************************************************************************
 ** the value a grid input's DynamicSelect is seeded with: the live Formik value
 ** when the field has one, else the widget's own loaded value.
 **
 ** The widget publishes its loaded values into Formik from an effect, which runs
 ** after render, while DynamicSelect captures its seed ONCE at mount.  A select
 ** whose mount wins that race captures undefined and shows empty forever - its
 ** remount key deliberately excludes the live Formik value, so nothing re-seeds
 ** it - even though the value lands in Formik a tick later and saves fine.  The
 ** fallback removes that ordering dependency.
 **
 ** Strictly undefined, not a nullish ??: a null Formik value is the user
 ** CLEARING the select this session, which must stay cleared.
 *******************************************************************************/
export function seedValueForSelect(formikValue: any, loadedValue: any): any
{
   return (formikValue === undefined ? loadedValue : formikValue);
}



/*******************************************************************************
 ** whether every one of a sub-row's fields is blank.
 **
 ** Such a sub-row is "nothing here" rather than "something half-entered": it is
 ** left out of the published JSON and not reported as missing required values.
 ** A sub-row with SOME of its fields filled in is a real error, and is reported.
 *******************************************************************************/
export function isSubRowBlank(values: Record<string, any>, cellFieldNames: string[], rowId: number, columnId: number, subRowId: number, gridId: number = DEFAULT_GRID_ID, widgetName: string = ""): boolean
{
   return ((cellFieldNames ?? []).every(fieldName => isBlank(values[cellFieldName(fieldName, rowId, columnId, subRowId, gridId, widgetName)])));
}


/*******************************************************************************
 ** every namespaced input name this structure generates - one per field per
 ** position, across all four axes and every grid.
 **
 ** The widget needs this to clear its own names out of the parent form when it
 ** re-seeds: ids are allocated from a counter that restarts at 1 on each seed,
 ** so a name generated by the previous structure can be handed to a different
 ** input by the next one, and a value left behind under it would then show up
 ** in a cell the user is looking at as blank.
 *******************************************************************************/
export function collectGeneratedFieldNames(structure: GridBuilderStructure, fieldNames: GridBuilderFieldNames): string[]
{
   const names: string[] = [];
   const widgetName      = widgetNameOf(structure);

   for (const gridId of gridIdsOf(structure))
   {
      for (const fieldName of fieldNames.gridHeader ?? [])
      {
         names.push(gridFieldName(fieldName, gridId, widgetName));
      }

      const axes = getGridAxes(structure, gridId);

      for (const columnId of axes.columnIds)
      {
         for (const fieldName of fieldNames.columnHeader ?? [])
         {
            names.push(columnFieldName(fieldName, columnId, gridId, widgetName));
         }
      }

      for (const rowId of axes.rowIds)
      {
         for (const fieldName of fieldNames.rowHeader ?? [])
         {
            names.push(rowFieldName(fieldName, rowId, gridId, widgetName));
         }

         for (const columnId of axes.columnIds)
         {
            for (const subRowId of axes.subRowIds[cellKey(rowId, columnId)] ?? [])
            {
               for (const fieldName of fieldNames.cell ?? [])
               {
                  names.push(cellFieldName(fieldName, rowId, columnId, subRowId, gridId, widgetName));
               }
            }
         }
      }
   }

   return (names);
}


/*******************************************************************************
 ** whether a whole grid is empty - no header value, no column or row header
 ** value, and no cell filled in.
 **
 ** This is what tells an override grid the user added and then walked away from
 ** apart from one they meant: a blank grid can be dropped on save rather than
 ** reported as a screen full of missing required values.
 *******************************************************************************/
export function isGridBlank(structure: GridBuilderStructure, values: Record<string, any>, gridId: number, fieldNames: GridBuilderFieldNames): boolean
{
   const widgetName = widgetNameOf(structure);

   for (const fieldName of fieldNames.gridHeader ?? [])
   {
      if (!isBlank(values[gridFieldName(fieldName, gridId, widgetName)]))
      {
         return (false);
      }
   }

   const axes = getGridAxes(structure, gridId);

   for (const columnId of axes.columnIds)
   {
      for (const fieldName of fieldNames.columnHeader ?? [])
      {
         if (!isBlank(values[columnFieldName(fieldName, columnId, gridId, widgetName)]))
         {
            return (false);
         }
      }
   }

   for (const rowId of axes.rowIds)
   {
      for (const fieldName of fieldNames.rowHeader ?? [])
      {
         if (!isBlank(values[rowFieldName(fieldName, rowId, gridId, widgetName)]))
         {
            return (false);
         }
      }

      for (const columnId of axes.columnIds)
      {
         for (const subRowId of axes.subRowIds[cellKey(rowId, columnId)] ?? [])
         {
            if (!isSubRowBlank(values, fieldNames.cell, rowId, columnId, subRowId, gridId, widgetName))
            {
               return (false);
            }
         }
      }
   }

   return (true);
}


/*******************************************************************************
 ** build structure, seed values, and sub-row record labels from the widget data
 ** the backend rendered.
 **
 ** recordLabels is keyed by cellFieldName("", ...) - the namespace of a sub-row
 ** with no field name - since a label belongs to the sub-row rather than to any
 ** one of its fields.
 **
 ** gridHeaderFieldCount and expectedGridCount say which kind of widget this data
 ** is for, as they do for emptyStructure: a widget that may hold more than one
 ** grid gives every one of them an allocated id, while a widget with exactly one
 ** grid and no grid header fields reads it at DEFAULT_GRID_ID and so round-trips
 ** through the names it has always used.
 **
 ** The data's own grid count is folded into that decision, so a payload
 ** describing several grids cannot land one of them on DEFAULT_GRID_ID however
 ** the widget was configured.  gridIdsAreAllocated is returned because the widget
 ** has to seed around the same answer, and restating it there is how the two
 ** would come to disagree.
 **
 ** widgetName is carried onto the structure, so every name built from it - here
 ** and in the widget - says which widget it belongs to.  See widgetSegment.
 *******************************************************************************/
export function structureFromWidgetData(widgetData: any, gridHeaderFieldCount: number = 0, expectedGridCount: number = 1, widgetName: string = ""): { structure: GridBuilderStructure, values: Record<string, any>, displayValues: Record<string, string>, recordLabels: Record<string, string>, gridIdsAreAllocated: boolean }
{
   const values: Record<string, any>           = {};
   const displayValues: Record<string, string> = {};
   const recordLabels: Record<string, string>  = {};

   ////////////////////////////////////////////////////////////////////////////////////
   // how many grids this widget may hold, folding the payload's own count into what //
   // the caller expected: a payload describing several grids is itself proof the    //
   // widget uses the dimension, whatever its configuration said.                    //
   ////////////////////////////////////////////////////////////////////////////////////
   const gridCount           = Math.max(expectedGridCount, widgetData?.grids?.length ?? 0);
   const gridIdsAreAllocated = usesAllocatedGridIds(gridHeaderFieldCount, gridCount);

   let structure = emptyStructure(gridHeaderFieldCount, gridCount, widgetName);

   //////////////////////////////////////////////////////////////////////////////////////
   // for a widget that reads its one grid at DEFAULT_GRID_ID, widget data with no     //
   // grids key describes that grid and IS it - the same reading the backend gives a   //
   // payload with no grids key - so it round-trips through the names it has always    //
   // used.                                                                            //
   //                                                                                  //
   // A widget whose grids carry allocated ids has no such reading: its grids are only //
   // ever the ones its grids list describes.  Falling back to the flat one for it     //
   // would fabricate a grid at DEFAULT_GRID_ID out of data that named no grids at     //
   // all.                                                                             //
   //////////////////////////////////////////////////////////////////////////////////////
   const gridDatas: any[] = widgetData?.grids ?? (gridIdsAreAllocated ? [] : [widgetData]);

   ////////////////////////////////////////////////////////////////////////////////////
   // an EXPLICITLY empty grids list is a widget with no grids, which a widget whose //
   // minGrids is 0 may legitimately be - a matrix that overrides nothing.  Absent   //
   // widget data is not that for a widget with one grid: it is read as the one grid //
   // it describes, per the note above, so the grid the structure starts with only   //
   // goes away when the data says so.                                               //
   ////////////////////////////////////////////////////////////////////////////////////
   if (gridDatas.length === 0)
   {
      structure = removeGrid(structure, DEFAULT_GRID_ID);
   }

   gridDatas.forEach((gridData, gridIndex) =>
   {
      let gridId = DEFAULT_GRID_ID;

      //////////////////////////////////////////////////////////////////////////////////////
      // every grid gets an allocated id unless this structure is the single-grid shape,  //
      // whose one grid is the default.  gridIndex cannot exceed 0 in that shape - a      //
      // payload of several grids is folded into gridCount above, which puts the          //
      // structure in the allocated-id shape - so the index test guards nothing reachable //
      // and only ever prevents the worse failure of two grids landing on one id.         //
      //////////////////////////////////////////////////////////////////////////////////////
      if (gridIdsAreAllocated || gridIndex > 0)
      {
         structure = addGrid(structure);
         gridId    = structure.gridIds[structure.gridIds.length - 1];
      }

      structure = loadGrid(structure, gridId, gridData, values, displayValues, recordLabels);
   });

   return ({structure, values, displayValues, recordLabels, gridIdsAreAllocated});
}


/*******************************************************************************
 ** read one grid of rendered widget data into a structure and its value maps
 *******************************************************************************/
function loadGrid(structure: GridBuilderStructure, gridId: number, gridData: any, values: Record<string, any>, displayValues: Record<string, string>, recordLabels: Record<string, string>): GridBuilderStructure
{
   const widgetName = widgetNameOf(structure);

   let result = structure;

   for (const fieldName of Object.keys(gridData?.header?.values ?? {}))
   {
      values[gridFieldName(fieldName, gridId, widgetName)] = gridData.header.values[fieldName];
   }
   for (const fieldName of Object.keys(gridData?.header?.displayValues ?? {}))
   {
      displayValues[gridFieldName(fieldName, gridId, widgetName)] = gridData.header.displayValues[fieldName];
   }

   for (const columnHeader of gridData?.columnHeaders ?? [])
   {
      result = addColumn(result, 0, gridId);
      const columnIds = getGridAxes(result, gridId).columnIds;
      const columnId  = columnIds[columnIds.length - 1];

      for (const fieldName of Object.keys(columnHeader?.values ?? {}))
      {
         values[columnFieldName(fieldName, columnId, gridId, widgetName)] = columnHeader.values[fieldName];
      }
      for (const fieldName of Object.keys(columnHeader?.displayValues ?? {}))
      {
         displayValues[columnFieldName(fieldName, columnId, gridId, widgetName)] = columnHeader.displayValues[fieldName];
      }
   }

   for (const dataRow of gridData?.rows ?? [])
   {
      result = addRow(result, 0, gridId);
      const rowIds = getGridAxes(result, gridId).rowIds;
      const rowId  = rowIds[rowIds.length - 1];

      for (const fieldName of Object.keys(dataRow?.header?.values ?? {}))
      {
         values[rowFieldName(fieldName, rowId, gridId, widgetName)] = dataRow.header.values[fieldName];
      }
      for (const fieldName of Object.keys(dataRow?.header?.displayValues ?? {}))
      {
         displayValues[rowFieldName(fieldName, rowId, gridId, widgetName)] = dataRow.header.displayValues[fieldName];
      }

      ////////////////////////////////////////////////////////////////////////////
      // cells are positional and parallel to columns, so the column at index i //
      // owns the cell at index i                                               //
      ////////////////////////////////////////////////////////////////////////////
      const columnIds = [...getGridAxes(result, gridId).columnIds];
      columnIds.forEach((columnId, columnIndex) =>
      {
         for (const subRow of dataRow?.cells?.[columnIndex]?.subRows ?? [])
         {
            result = addSubRow(result, rowId, columnId, gridId);
            const subRowIds = getGridAxes(result, gridId).subRowIds[cellKey(rowId, columnId)];
            const subRowId  = subRowIds[subRowIds.length - 1];

            for (const fieldName of Object.keys(subRow?.values ?? {}))
            {
               values[cellFieldName(fieldName, rowId, columnId, subRowId, gridId, widgetName)] = subRow.values[fieldName];
            }

            /////////////////////////////////////////////////////////////////////////////
            // display values matter for possible-value fields: without them an        //
            // already-selected itemId renders as a blank dropdown rather than its SKU //
            /////////////////////////////////////////////////////////////////////////////
            for (const fieldName of Object.keys(subRow?.displayValues ?? {}))
            {
               displayValues[cellFieldName(fieldName, rowId, columnId, subRowId, gridId, widgetName)] = subRow.displayValues[fieldName];
            }

            if (subRow?.recordLabel)
            {
               recordLabels[cellFieldName("", rowId, columnId, subRowId, gridId, widgetName)] = subRow.recordLabel;
            }
         }
      });
   }

   return (result);
}


/*******************************************************************************
 ** the widget data that would seed exactly the given structure, values and
 ** display values - the shape a renderer sends and structureFromWidgetData
 ** reads.  What lets a modal editor's cancel put the grid back the way it was
 ** when the editor opened: re-seeding from this is the same path a fresh load
 ** takes, so nothing about the grid's state is left half-restored.
 **
 ** fields names each axis's fields; usesGrids says whether to describe the grids
 ** list (a widget with the grids dimension) or the one flat grid.
 *******************************************************************************/
export function snapshotAsWidgetData(structure: GridBuilderStructure, values: Record<string, any>, displayValues: Record<string, string>, usesGrids: boolean, fields: { gridHeader: string[], columnHeader: string[], rowHeader: string[], cell: string[] }, widgetName: string = ""): any
{
   const record = (names: string[], nameFor: (fieldName: string) => string) =>
   {
      const recordValues: Record<string, any>           = {};
      const recordDisplayValues: Record<string, string> = {};
      for (const fieldName of names)
      {
         const name = nameFor(fieldName);
         if (values[name] !== undefined)
         {
            recordValues[fieldName] = values[name];
         }
         if (displayValues[name] !== undefined)
         {
            recordDisplayValues[fieldName] = displayValues[name];
         }
      }
      return ({values: recordValues, displayValues: recordDisplayValues});
   };

   const describeGrid = (gridId: number) =>
   {
      const axes = getGridAxes(structure, gridId);
      return ({
         header: record(fields.gridHeader, fieldName => gridFieldName(fieldName, gridId, widgetName)),
         columnHeaders: axes.columnIds.map(columnId => record(fields.columnHeader, fieldName => columnFieldName(fieldName, columnId, gridId, widgetName))),
         rows: axes.rowIds.map(rowId => ({
            header: record(fields.rowHeader, fieldName => rowFieldName(fieldName, rowId, gridId, widgetName)),
            cells: axes.columnIds.map(columnId => ({
               subRows: (axes.subRowIds[cellKey(rowId, columnId)] ?? []).map(subRowId => record(fields.cell, fieldName => cellFieldName(fieldName, rowId, columnId, subRowId, gridId, widgetName))),
            })),
         })),
      });
   };

   if (usesGrids)
   {
      return ({grids: structure.gridIds.map(gridId => describeGrid(gridId))});
   }

   const {columnHeaders, rows} = describeGrid(DEFAULT_GRID_ID);
   return ({columnHeaders, rows});
}


/*******************************************************************************
 ** a cell, by its position
 *******************************************************************************/
export interface GridBuilderCellPosition
{
   gridId: number;
   rowId: number;
   columnId: number;
}


/*******************************************************************************
 ** whether a row has anything in it - a row header value, or a sub-row with a
 ** value in any cell.  A row with neither is not yet something the user is
 ** describing.
 *******************************************************************************/
export function rowHasContent(structure: GridBuilderStructure, values: Record<string, any>, rowHeaderFieldNames: string[], cellFieldNames: string[], rowId: number, gridId: number, widgetName: string): boolean
{
   if ((rowHeaderFieldNames ?? []).some(fieldName => !isBlank(values[rowFieldName(fieldName, rowId, gridId, widgetName)])))
   {
      return (true);
   }

   const axes = getGridAxes(structure, gridId);
   return (axes.columnIds.some(columnId => (axes.subRowIds[cellKey(rowId, columnId)] ?? [])
      .some(subRowId => !isSubRowBlank(values, cellFieldNames ?? [], rowId, columnId, subRowId, gridId, widgetName))));
}


/*******************************************************************************
 ** how many of a grid's rows have anything in them.  A row's mere presence does
 ** not satisfy a minimum row count: a grid opens with blank rows ready to fill
 ** in, and a row left that way is published with nothing in it.
 *******************************************************************************/
export function countRowsWithContent(structure: GridBuilderStructure, values: Record<string, any>, rowHeaderFieldNames: string[], cellFieldNames: string[], gridId: number = DEFAULT_GRID_ID, widgetName: string = ""): number
{
   return (getGridAxes(structure, gridId).rowIds
      .filter(rowId => rowHasContent(structure, values, rowHeaderFieldNames, cellFieldNames, rowId, gridId, widgetName)).length);
}


/*******************************************************************************
 ** the cells holding fewer FILLED sub-rows than the widget requires, in rows
 ** that have anything in them.
 **
 ** A wholly blank sub-row is dropped when the grid is published, so it cannot
 ** count toward minSubRowsPerCell - without this, a row whose header is filled
 ** in and whose only sub-row is blank passes every per-field check (a required
 ** cell field is only demanded of a sub-row that is partly filled) and is saved
 ** with nothing in it.  A row that is entirely blank is left alone: it is not
 ** yet something the user is describing.
 *******************************************************************************/
export function findCellsShortOfSubRows(structure: GridBuilderStructure, values: Record<string, any>, rowHeaderFieldNames: string[], cellFieldNames: string[], minSubRowsPerCell: number, widgetName: string = ""): GridBuilderCellPosition[]
{
   if (!(minSubRowsPerCell > 0) || (cellFieldNames ?? []).length === 0)
   {
      return ([]);
   }

   const shortCells: GridBuilderCellPosition[] = [];
   for (const gridId of structure.gridIds)
   {
      const axes = getGridAxes(structure, gridId);
      for (const rowId of axes.rowIds)
      {
         if (!rowHasContent(structure, values, rowHeaderFieldNames, cellFieldNames, rowId, gridId, widgetName))
         {
            continue;
         }

         axes.columnIds.forEach(columnId =>
         {
            const filledCount = (axes.subRowIds[cellKey(rowId, columnId)] ?? [])
               .filter(subRowId => !isSubRowBlank(values, cellFieldNames, rowId, columnId, subRowId, gridId, widgetName)).length;
            if (filledCount < minSubRowsPerCell)
            {
               shortCells.push({gridId, rowId, columnId});
            }
         });
      }
   }
   return (shortCells);
}


/*******************************************************************************
 ** whether two published GridBuilder values describe the same grid - compared
 ** as parsed JSON with object keys in a fixed order, since a value a backend
 ** stored and handed back need not keep the key order the widget wrote it in.
 ** False when either is absent or not JSON.
 *******************************************************************************/
export function isSameGridJson(a: any, b: any): boolean
{
   const canonical = (value: any): any =>
   {
      if (Array.isArray(value))
      {
         return (value.map(canonical));
      }
      if (value !== null && typeof value === "object")
      {
         return (Object.keys(value).sort().reduce((result: Record<string, any>, key) =>
         {
            result[key] = canonical(value[key]);
            return (result);
         }, {}));
      }
      return (value);
   };

   if (typeof a !== "string" || typeof b !== "string")
   {
      return (false);
   }

   try
   {
      return (JSON.stringify(canonical(JSON.parse(a))) === JSON.stringify(canonical(JSON.parse(b))));
   }
   catch (e)
   {
      return (false);
   }
}


/*******************************************************************************
 ** a row whose upper bound is wrong, as found by findRowUpperBoundProblems.
 *******************************************************************************/
export interface RowUpperBoundProblem
{
   gridId: number;
   rowId: number;
   max: number | null;
   problem: "notPositiveWholeNumber" | "duplicate" | "secondNoLimit";
}


/*******************************************************************************
 ** the rows, within each grid, whose upper bound is wrong - for a widget whose
 ** rows are bands defined only by their upper bound, each covering everything
 ** above the previous row's bound up to its own (rowUpperBoundFieldName).
 **
 ** A bound must be a whole number of 1 or more, no two rows may share one, and
 ** a blank bound - no upper limit - is allowed on only one row: the rows are
 ** sorted ascending with blanks last, so that row is the last band.
 **
 ** Only rows with something in them are judged.  A wholly blank row - the one a
 ** new grid starts with, say - is not yet a band; countRowsWithContent is what
 ** keeps a grid of nothing but blank rows from being saved.
 *******************************************************************************/
export function findRowUpperBoundProblems(structure: GridBuilderStructure, values: Record<string, any>, upperBoundFieldName: string, rowHeaderFieldNames: string[], cellFieldNames: string[], widgetName: string = ""): RowUpperBoundProblem[]
{
   if (!upperBoundFieldName)
   {
      return ([]);
   }

   const problems: RowUpperBoundProblem[] = [];
   for (const gridId of structure.gridIds)
   {
      const seenBounds = new Set<number>();
      const duplicated = new Set<number>();
      let sawNoLimit   = false;

      for (const rowId of getGridAxes(structure, gridId).rowIds)
      {
         if (!rowHasContent(structure, values, rowHeaderFieldNames, cellFieldNames, rowId, gridId, widgetName))
         {
            continue;
         }

         const value = values[rowFieldName(upperBoundFieldName, rowId, gridId, widgetName)];
         if (isBlank(value))
         {
            if (sawNoLimit)
            {
               problems.push({gridId, rowId, max: null, problem: "secondNoLimit"});
            }
            sawNoLimit = true;
            continue;
         }

         const max = Number(value);
         if (!Number.isInteger(max) || max < 1)
         {
            problems.push({gridId, rowId, max: Number.isNaN(max) ? null : max, problem: "notPositiveWholeNumber"});
         }
         else if (seenBounds.has(max))
         {
            ////////////////////////////////////////////////////////////////////////
            // once per bound, however many rows repeat it - the message names the //
            // bound, so a third copy would only say the same thing again          //
            ////////////////////////////////////////////////////////////////////////
            if (!duplicated.has(max))
            {
               problems.push({gridId, rowId, max, problem: "duplicate"});
               duplicated.add(max);
            }
         }
         seenBounds.add(max);
      }
   }
   return (problems);
}


/*******************************************************************************
 ** a key for each row in the structure, unique across its grids - what
 ** keepOnlyRows takes.
 *******************************************************************************/
export function rowKeysOf(structure: GridBuilderStructure): Set<string>
{
   const keys = new Set<string>();
   for (const gridId of structure.gridIds)
   {
      getGridAxes(structure, gridId).rowIds.forEach(rowId => keys.add(`${gridId}.${rowId}`));
   }
   return (keys);
}


/*******************************************************************************
 ** the structure with only the rows named by rowKeys (from rowKeysOf) - so a
 ** check can be run over the rows that were there at some earlier moment, and
 ** say nothing about ones added since.
 *******************************************************************************/
export function keepOnlyRows(structure: GridBuilderStructure, rowKeys: Set<string>): GridBuilderStructure
{
   const result = copy(structure);
   for (const gridId of result.gridIds)
   {
      const axes  = getGridAxesForWrite(result, gridId);
      axes.rowIds = axes.rowIds.filter(rowId => rowKeys.has(`${gridId}.${rowId}`));
   }
   return (result);
}
